import { cn } from '@/lib/utils';
import { ARTIFACT_EFFECT_META } from '@/components/artifacts/demo/artifact-effect-meta';
import type { ArtifactType, ArtifactCategory } from '@/config/objects/artifacts';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  FormFieldGroup,
  Input,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

interface EffectTeam {
  id: string;
  name: string;
  iconColor: string;
  isStaff?: boolean;
}

interface FacilitatorEffectDialogProps {
  eventId: string;
  teams: EffectTeam[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface ApplyEffectResponse {
  data: {
    effects: { id: string }[];
    shieldedTeamIds: string[];
    steal?: { stolenTotal: number };
  };
}

const CATEGORY_ORDER: ArtifactCategory[] = ['offensive', 'defensive', 'wildcard'];

const EFFECT_TYPES_BY_CATEGORY: Record<ArtifactCategory, ArtifactType[]> = {
  offensive: ['scramble', 'freeze', 'blackout', 'steal', 'misdirect'],
  defensive: ['shield'],
  wildcard: ['double_down'],
};

const EFFECT_LABELS: Record<ArtifactType, string> = {
  scramble: 'Scramble',
  freeze: 'Freeze',
  blackout: 'Blackout',
  steal: 'Steal',
  misdirect: 'Misdirect',
  double_down: 'Double Down',
  shield: 'Shield',
};

const CATEGORY_STYLE: Record<ArtifactCategory, { label: string; selected: string; icon: string }> =
  {
    offensive: { label: 'Offensive', selected: 'border-red-500 bg-red-50', icon: 'text-red-600' },
    defensive: {
      label: 'Defensive',
      selected: 'border-blue-500 bg-blue-50',
      icon: 'text-blue-600',
    },
    wildcard: {
      label: 'Wildcard',
      selected: 'border-purple-500 bg-purple-50',
      icon: 'text-purple-600',
    },
  };

// Types with no timed window on the server — hide the duration field.
const UNTIMED_TYPES = new Set<ArtifactType>(['steal']);
const DEFAULT_STEAL_POINTS = '100';

/**
 * Admin/facilitator tool: apply an artifact effect (power-up) to one or
 * more teams — or all of them. Posts to the shared
 * `POST /events/:id/effects/apply` endpoint; the admin (Clerk) auth path
 * records the effect with a "Game Master" source. Target teams see the
 * effect live on their device.
 */
export function FacilitatorEffectDialog({
  eventId,
  teams,
  open,
  onOpenChange,
}: FacilitatorEffectDialogProps) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const [effectType, setEffectType] = useState<ArtifactType | null>(null);
  const [durationText, setDurationText] = useState('');
  const [pointsText, setPointsText] = useState(DEFAULT_STEAL_POINTS);
  const [allTeams, setAllTeams] = useState(true);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Staff teams are never valid targets.
  const targetableTeams = useMemo(() => teams.filter(t => !t.isStaff), [teams]);

  const isSteal = effectType === 'steal';
  const isTimed = !!effectType && !UNTIMED_TYPES.has(effectType);
  const pointsValue = Number.parseInt(pointsText, 10);
  const durationValue = Number.parseInt(durationText, 10);

  const targetCount = allTeams ? targetableTeams.length : selectedIds.length;
  const pointsValid = !isSteal || (Number.isFinite(pointsValue) && pointsValue >= 0);
  const durationValid =
    !isTimed ||
    durationText.trim() === '' ||
    (Number.isFinite(durationValue) && durationValue >= 1);

  const applyMutation = useMutation({
    mutationFn: async () => {
      if (!effectType) throw new Error('Select an effect');
      return apiClient.post(`/events/${eventId}/effects/apply`, {
        effectType,
        teamIds: allTeams ? [] : selectedIds,
        durationMinutes: isTimed && durationText.trim() !== '' ? durationValue : null,
        effectValue: isSteal ? pointsValue : null,
      }) as Promise<ApplyEffectResponse>;
    },
    onSuccess: res => {
      const applied = res.data.effects.length;
      const shielded = res.data.shieldedTeamIds.length;
      const parts = [`Applied to ${applied} team${applied === 1 ? '' : 's'}.`];
      if (shielded > 0) parts.push(`${shielded} blocked by a shield.`);
      if (res.data.steal && res.data.steal.stolenTotal > 0) {
        parts.push(`${res.data.steal.stolenTotal} points removed.`);
      }
      toast.success(parts.join(' '));
      queryClient.invalidateQueries({ queryKey: ['EventTeams', eventId] });
      onOpenChange(false);
      reset();
    },
    onError: (err: Error) => {
      toast.error(err?.message || 'Failed to apply effect');
    },
  });

  const reset = () => {
    setEffectType(null);
    setDurationText('');
    setPointsText(DEFAULT_STEAL_POINTS);
    setAllTeams(true);
    setSelectedIds([]);
  };

  const toggleTeam = (teamId: string) => {
    setSelectedIds(prev =>
      prev.includes(teamId) ? prev.filter(id => id !== teamId) : [...prev, teamId],
    );
  };

  const canApply =
    !!effectType && targetCount > 0 && pointsValid && durationValid && !applyMutation.isPending;

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        onOpenChange(next);
        if (!next) reset();
      }}
    >
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Apply Effect</DialogTitle>
          <DialogDescription>
            Apply an artifact effect to one or more teams. Targets see it live on their device.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Effect picker */}
          <div className="space-y-3">
            {CATEGORY_ORDER.map(category => {
              const style = CATEGORY_STYLE[category];
              return (
                <div key={category} className="space-y-1.5">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {style.label}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {EFFECT_TYPES_BY_CATEGORY[category].map(type => {
                      const Icon = ARTIFACT_EFFECT_META[type].icon;
                      const selected = effectType === type;
                      return (
                        <button
                          key={type}
                          type="button"
                          onClick={() => setEffectType(type)}
                          aria-pressed={selected}
                          className={cn(
                            'flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors',
                            selected ? style.selected : 'border-border bg-card hover:bg-muted/50',
                          )}
                        >
                          <Icon
                            className={cn(
                              'h-4 w-4',
                              selected ? style.icon : 'text-muted-foreground',
                            )}
                          />
                          <span className="font-medium">{EFFECT_LABELS[type]}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            {effectType && (
              <p className="rounded-lg bg-muted/50 p-3 text-sm text-muted-foreground">
                {ARTIFACT_EFFECT_META[effectType].summary}
              </p>
            )}
          </div>

          {/* Duration (timed effects only) */}
          {isTimed && (
            <FormFieldGroup
              name="duration"
              label="Duration (minutes)"
              description="Leave blank to use the effect's default."
              error={!durationValid ? 'Enter 1 minute or more' : undefined}
            >
              <Input
                id="duration"
                type="number"
                min={1}
                value={durationText}
                onChange={e => setDurationText(e.target.value)}
                placeholder="Default"
              />
            </FormFieldGroup>
          )}

          {/* Points (steal only) */}
          {isSteal && (
            <FormFieldGroup
              name="points"
              label="Points to remove"
              description="Removed from each team, clamped so no score goes below zero."
              error={!pointsValid ? 'Enter 0 or more' : undefined}
            >
              <Input
                id="points"
                type="number"
                min={0}
                value={pointsText}
                onChange={e => setPointsText(e.target.value)}
                placeholder="100"
              />
            </FormFieldGroup>
          )}

          {/* Team targeting */}
          <div className="space-y-2">
            <p className="text-sm font-medium">Teams</p>
            <label className="flex items-center gap-2 rounded-lg border border-border p-3 text-sm">
              <Checkbox
                checked={allTeams}
                onCheckedChange={checked => setAllTeams(checked === true)}
              />
              <span>All teams ({targetableTeams.length})</span>
            </label>

            {!allTeams && (
              <div className="max-h-52 space-y-1 overflow-y-auto rounded-lg border border-border p-2">
                {targetableTeams.length === 0 ? (
                  <p className="p-2 text-sm text-muted-foreground">No teams to target.</p>
                ) : (
                  targetableTeams.map(team => (
                    <label
                      key={team.id}
                      className="flex items-center gap-2 rounded-md px-2 py-2 text-sm hover:bg-muted/50"
                    >
                      <Checkbox
                        checked={selectedIds.includes(team.id)}
                        onCheckedChange={() => toggleTeam(team.id)}
                      />
                      <span
                        className="h-2.5 w-2.5 rounded-full"
                        style={{ backgroundColor: team.iconColor }}
                      />
                      <span>{team.name}</span>
                    </label>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => applyMutation.mutate()}
            disabled={!canApply}
            loading={applyMutation.isPending}
          >
            {effectType
              ? `Apply ${EFFECT_LABELS[effectType]}${targetCount > 0 ? ` to ${targetCount}` : ''}`
              : 'Apply Effect'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
